import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import {
  AtoN,
  AtonApi,
  Configuration,
  type GetAtoNs200Response,
  type UserGroupUid,
} from '@ais-ui/api-spec/config';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { getBasePath } from '../getAISBasePath';

const useAtonApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const atonApi = useMemo(() => new AtonApi(configuration), [configuration]);
  return atonApi;
};

export const useFetchAllAtons = (): UseQueryResult<GetAtoNs200Response> => {
  const atonApi = useAtonApi();
  return useQuery(['FetchAllAtons'], async () => {
    const result = await atonApi.getAtoNs();

    if (result) {
      convertFieldsToDates(result, []);
    }

    return result;
  });
};

export const useCreateAton = (): UseMutationResult<
  UserGroupUid,
  Error,
  AtoN,
  unknown
> => {
  const atonApi = useAtonApi();
  const queryClient = useQueryClient();

  return useMutation(
    (aton: AtoN) => {
      return atonApi.addAtoN({ atoN: aton });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['FetchAllAtons']),
    }
  );
};

type UpdateAtoNParams = {
  id: string;
  aton: AtoN;
};

export const useUpdateAton = (): UseMutationResult<
  void,
  Error,
  UpdateAtoNParams,
  unknown
> => {
  const atonApi = useAtonApi();
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, aton }: UpdateAtoNParams) => {
      aton.uuid = id;
      return atonApi.updateAtoN({ uuid: id, atoN: aton });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['FetchAllAtons']),
    }
  );
};

export const useDeleteAton = (): UseMutationResult<
  void,
  Error,
  string,
  unknown
> => {
  const atonApi = useAtonApi();
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => {
      return atonApi.deleteAtoN({ uuid: id });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['FetchAllAtons']),
    }
  );
};
